<template>
  <div class="match3-challenge">
    <div class="match-board">
      <!-- Instructions -->
      <p class="instructions">Tap a face and then a name to make a match</p>

      <!-- Matching Area -->
      <div class="matching-area">
        <!-- Photos Column -->
        <div class="photos-column">
          <h4 class="column-title">Faces</h4>
          <div class="photos-list">
            <div
              v-for="person in challenge.people"
              :key="'photo-' + person.id"
              class="photo-item"
              :class="{ selected: selectedFace === person.id, matched: !!matches[person.id] }"
              :data-person-id="person.id"
              @click="selectFace(person.id)"
            >
              <img :src="person.photoUrl" :alt="person.name" class="match-photo">
              <!-- Show matched name if exists -->
              <div v-if="matches[person.id]" class="matched-name">
                {{ getPersonName(matches[person.id]) }}
                <button class="unmatch-btn" :disabled="!!feedback" @click="unmatch(person.id)">
                  x
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Names Column -->
        <div class="names-column">
          <h4 class="column-title">Names</h4>
          <div class="names-list">
            <div
              v-for="person in challenge.names || challenge.people"
              :key="'name-' + person.id"
              class="name-item"
              :class="{
                matched: isNameMatched(person.id),
                selected: selectedName === person.id
              }"
              @click="selectName(person.id)"
            >
              {{ person.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Button removed: auto-submit when all matches selected -->

    <!-- Feedback removed -->
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
const props = defineProps({
  challenge: Object,
  feedback: Object
})

const emit = defineEmits(['answer'])

const matches = ref({}) // photoId -> nameId
const autoSubmitted = ref(false)
const selectedFace = ref(null)
const selectedName = ref(null)

function selectFace(personId) {
  if (props.feedback || matches.value[personId]) return
  if (selectedFace.value === personId) {
    selectedFace.value = null
    return
  }
  if (selectedName.value) {
    setMatch(personId, selectedName.value)
    selectedFace.value = null
    selectedName.value = null
  } else {
    selectedFace.value = personId
  }
}

function selectName(personId) {
  if (props.feedback || isNameMatched(personId)) return
  if (selectedName.value === personId) {
    selectedName.value = null
    return
  }
  if (selectedFace.value) {
    setMatch(selectedFace.value, personId)
    selectedFace.value = null
    selectedName.value = null
  } else {
    selectedName.value = personId
  }
}

function setMatch(photoId, nameId) {
  // Remove any existing match for this name
  Object.keys(matches.value).forEach(key => {
    if (matches.value[key] === nameId) {
      delete matches.value[key]
    }
  })
  matches.value[photoId] = nameId
}

function unmatch(photoPersonId) {
  delete matches.value[photoPersonId]
}

function isNameMatched(namePersonId) {
  return Object.values(matches.value).includes(namePersonId)
}

function getPersonName(personId) {
  const person = props.challenge.people.find(p => p.id === personId)
  return person?.name || ''
}

watch(
  () => Object.keys(matches.value).length,
  val => {
    if (!autoSubmitted.value && val === props.challenge.people.length) {
      autoSubmitted.value = true
      submitMatches()
    }
  }
)

function submitMatches() {
  // Convert matches to the expected format for the store
  const answer = {}
  Object.keys(matches.value).forEach(photoId => {
    const nameId = matches.value[photoId]
    answer[photoId] = nameId
  })

  emit('answer', answer)
}
</script>

<style scoped>
.match3-challenge {
  @apply flex justify-center;
}

.match-board {
  width: min(100%, 16.5rem);
}

.instructions {
  @apply mb-3 text-center text-sm text-gray-600;
}

.matching-area {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 7.25rem));
  gap: 0.75rem;
  justify-content: center;
}

.column-title {
  @apply text-base font-semibold text-primary mb-3 text-center;
}

.photos-list,
.names-list {
  @apply grid gap-2;
}

.photo-item {
  @apply relative aspect-square bg-white rounded-lg p-2 border-2 border-gray-200 transition-all duration-200 cursor-pointer overflow-hidden;
}

.photo-item[data-person-id] {
  @apply hover:border-accent;
}

.match-photo {
  @apply w-full h-full object-cover rounded-lg;
}

.matched-name {
  @apply absolute inset-x-2 bottom-2 flex items-center justify-between gap-1 bg-white rounded px-2 py-1 text-xs font-medium text-primary shadow;
  opacity: 0.95;
}

.unmatch-btn {
  @apply text-red-500 hover:text-red-700 font-bold text-xs leading-none;
}

.name-item {
  @apply flex items-center justify-center aspect-square bg-white rounded-lg p-3 border-2 border-gray-200 text-center text-sm font-medium cursor-pointer transition-all duration-200 select-none;
}

.name-item:not(.matched):hover {
  @apply border-accent bg-secondary-fixed;
}

.name-item.matched {
  @apply opacity-50 cursor-not-allowed bg-gray-100;
}

.name-item.selected {
  @apply border-accent bg-secondary-fixed;
}

.photo-item.selected {
  @apply border-accent;
}

.photo-item.matched {
  @apply border-green-400;
}

.submit-section {
  @apply mt-6;
}

.feedback-section {
  @apply mt-6 text-center;
}

.feedback-message {
  @apply inline-flex items-center gap-3 px-4 py-3 rounded-lg;
}

.feedback-message.correct {
  @apply bg-green-100 text-green-800;
}

.feedback-message.incorrect {
  @apply bg-red-100 text-red-800;
}

.feedback-icon {
  @apply text-2xl font-bold;
}

.feedback-title {
  @apply font-semibold;
}

.xp-gained {
  @apply text-sm font-medium;
}

.correct-matches {
  @apply bg-blue-50 rounded-lg p-4;
}

.correct-matches-grid {
  @apply grid grid-cols-1 gap-2;
}

.correct-match-item {
  @apply flex items-center gap-3 bg-white rounded-lg p-2;
}

.mini-photo {
  @apply w-8 h-8 rounded-full object-cover;
}

.mini-name {
  @apply text-sm font-medium;
}
</style>
